import React from 'react'
import './white-label-editor.scss'
import * as deepEqual from 'fast-deep-equal'
import classnames from 'classnames'
import { Drawer } from '@pushly/aqe/lib/components'
import { useRefState } from '@halo/hooks/use-ref-state'
import { EyeOutlined, LinkOutlined, WarningFilled } from '@ant-design/icons'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Col, Input, notification, Popover, Row, Select, Switch } from 'antd'
import { useDebouncer } from '@halo/hooks/use-debouncer'
import WhiteLabelModel, { IFaviconResource } from '@halo/models/white-label.model'
import { useService } from '@halo/hooks/use-service'
import { AppState } from '@halo/stores/app.state'
import { Flags } from '@pushly/aquarium-utils/lib/enums/flags'
import ColorPickerPopover from '@halo/components/color-picker-popover/color-picker-popover'
import aqe from '@pushly/aqe'

interface IWhiteLabelEditor {
    className?: string
    mode?: 'edit' | 'create'
    whiteLabel?: WhiteLabelModel
    visible?: boolean
    submitText?: string
    onSubmit?: (whiteLabel: any) => void
    onClose?: () => void
    onVisibilityChange?: (visible?: boolean) => void
}

interface IValidationResponse {
    ok: boolean
    errors: string[]
}
const setNotOk = (validation: IValidationResponse, msg: string) => {
    validation.ok = false
    validation.errors.push(msg)
}

const validateSubmit = (whiteLabel: WhiteLabelModel): IValidationResponse => {
    const res: IValidationResponse = { ok: true, errors: [] }

    if (!whiteLabel.getDisplayName()?.trim()) {
        setNotOk(res, 'Display name is required.')
    }

    if (!whiteLabel.getHostname()?.trim()) {
        setNotOk(res, 'Hostname is required.')
    }

    if (!whiteLabel.getPreviewDomainId()) {
        setNotOk(res, 'A valid Preview Domain is required.')
    }

    /**
     * The following are optional:
     * validation is handled at the api layer
     */
    // setSupportEmailAddress
    // setDocumentationLink
    // setLoginBackgroundUrl
    // setFaviconResources
    // setLogoUrl
    // setPrimaryIconUrl
    // setSidebarIconUrl
    // setPrimaryColorHex
    // setSecondaryColorHex
    // setTertiaryColorHex

    return res
}

const buildSubmitPackage = (propsWhiteLabel: WhiteLabelModel | undefined, workingWhiteLabel: WhiteLabelModel) => {
    const dto: any = {}

    const compareAndSetNullableString = (
        key: string,
        prev: string | null,
        next: string | null,
        emptyAsNull = false,
    ) => {
        // ensure undefined|empty is treated as null based on params
        if ((!emptyAsNull && (next ?? null) !== (prev ?? null)) || (emptyAsNull && (next || null) !== (prev || null))) {
            dto[key] = next?.trim() ?? null

            if (emptyAsNull && !dto[key]) {
                dto[key] = null
            }
        }
    }

    if (workingWhiteLabel.getIsActive() !== propsWhiteLabel?.getIsActive()) {
        dto.isActive = workingWhiteLabel.getIsActive()
    }

    const displayName = workingWhiteLabel.getDisplayName()
    if (!!displayName && displayName !== propsWhiteLabel?.getDisplayName()) {
        dto.displayName = displayName.trim()
    }

    const hostname = workingWhiteLabel.getHostname()
    if (!!hostname && hostname !== propsWhiteLabel?.getHostname()) {
        dto.hostname = hostname.trim()
    }

    const previewDomainId = workingWhiteLabel.getPreviewDomainId()
    if (!!previewDomainId && previewDomainId !== propsWhiteLabel?.getPreviewDomainId()) {
        dto.previewDomainId = previewDomainId
    }

    // documentation
    compareAndSetNullableString(
        'documentationLink',
        propsWhiteLabel?.getDocumentationLink(),
        workingWhiteLabel.getDocumentationLink(),
        true,
    )
    compareAndSetNullableString(
        'supportEmailAddress',
        propsWhiteLabel?.getSupportEmailAddress(),
        workingWhiteLabel.getSupportEmailAddress(),
        true,
    )

    // media
    compareAndSetNullableString('logoUrl', propsWhiteLabel?.getLogoUrl(), workingWhiteLabel.getLogoUrl(), true)
    compareAndSetNullableString(
        'primaryIconUrl',
        propsWhiteLabel?.getPrimaryIconUrl(),
        workingWhiteLabel.getPrimaryIconUrl(),
        true,
    )
    compareAndSetNullableString(
        'sidebarIconUrl',
        propsWhiteLabel?.getSidebarIconUrl(),
        workingWhiteLabel.getSidebarIconUrl(),
        true,
    )
    compareAndSetNullableString(
        'loginBackgroundUrl',
        propsWhiteLabel?.getLoginBackgroundUrl(),
        workingWhiteLabel.getLoginBackgroundUrl(),
        true,
    )

    const faviconResources = workingWhiteLabel.getFaviconResources() ?? {}
    const prevFaviconResources = propsWhiteLabel?.getFaviconResources() ?? {}
    if (!deepEqual(prevFaviconResources, faviconResources)) {
        dto.faviconResources = faviconResources
    }

    // colors
    compareAndSetNullableString(
        'primaryColorHex',
        propsWhiteLabel?.getPrimaryColorHex(),
        workingWhiteLabel.getPrimaryColorHex(),
    )
    compareAndSetNullableString(
        'secondaryColorHex',
        propsWhiteLabel?.getSecondaryColorHex(),
        workingWhiteLabel.getSecondaryColorHex(),
    )
    compareAndSetNullableString(
        'tertiaryColorHex',
        propsWhiteLabel?.getTertiaryColorHex(),
        workingWhiteLabel.getTertiaryColorHex(),
    )

    return dto
}

const MediaFormItem = (props: { label: string; value: string | null; onChange: (v: string | null) => void }) => {
    return (
        <Form.Item
            label={
                <span>
                    {props.label}
                    {!!props.value && (
                        <Popover
                            className="media-preview"
                            trigger="click"
                            content={
                                <img
                                    src={
                                        /^\/assets\//.test(props.value)
                                            ? `${aqe.defaults.publicPlatformDomain}${props.value}`
                                            : props.value
                                    }
                                    alt={props.label}
                                    style={{
                                        maxHeight: 400,
                                        maxWidth: 400,
                                    }}
                                />
                            }
                        >
                            <EyeOutlined />
                        </Popover>
                    )}
                </span>
            }
        >
            <Input
                defaultValue={props.value}
                onChange={(ev) => {
                    const url = ev.target.value
                    props.onChange(url)
                }}
            />
        </Form.Item>
    )
}

const WhiteLabelEditor = (props: IWhiteLabelEditor) => {
    const mode = props.mode ?? 'create'
    const isCreateMode = mode === 'create'

    const appState = useService<AppState>(AppState)
    const [debounce] = useDebouncer()
    const [tmpValue, setTmpValue] = useRefState<WhiteLabelModel>(props.whiteLabel?.clone() ?? WhiteLabelModel.build({}))

    const value = tmpValue.current.clone()
    const currResources = value.getFaviconResources() ?? []
    const currFaviconUrl = currResources[0]?.href
    const submitDisabled = Object.keys(buildSubmitPackage(props.whiteLabel, value)).length === 0

    const availablePreviewDomains =
        appState.currentUserDomains.filter((d) => d.flags?.includes(Flags.domain.PREVIEW_DOMAIN.key) ?? false) ?? []

    return (
        <Drawer
            className={classnames('white-label-editor', `mode-${mode}`, props.className, {
                visible: props.visible,
            })}
            title={`${isCreateMode ? 'Create' : 'Edit'} White-Label`}
            visible={props.visible ?? false}
            afterVisibleChange={props.onVisibilityChange}
            disableSubmit={submitDisabled}
            submitText={props.submitText ?? (isCreateMode ? 'Submit' : 'Save')}
            onClose={() => props.onClose?.()}
            onSubmit={() => {
                const propsWhiteLabel = props.whiteLabel
                const workingWhiteLabel = tmpValue.current
                const res = validateSubmit(workingWhiteLabel.clone())

                if (res.ok) {
                    return props.onSubmit?.(buildSubmitPackage(propsWhiteLabel, workingWhiteLabel))
                } else {
                    notification.destroy()
                    notification.error({
                        message: '',
                        description: res.errors.map((err, idx) => (
                            <div key={idx}>
                                <span>{err}</span>
                            </div>
                        )),
                    })
                }
            }}
        >
            <Form layout="horizontal">
                <div className="switch-row">
                    <div className="switch-left">
                        <span>White-Label Enabled:</span>
                    </div>
                    <div className="switch-right">
                        <Switch
                            size="small"
                            checked={value.getIsActive()}
                            onChange={(v) => {
                                value.setIsActive(v)
                                setTmpValue(value)
                            }}
                        />
                    </div>
                </div>

                <Form.Item label="Display name">
                    <Input
                        defaultValue={value.getDisplayName()}
                        onChange={(ev) => {
                            const name = ev.target.value
                            debounce(() => {
                                value.setDisplayName(name)
                                setTmpValue(value)
                            })
                        }}
                    />
                </Form.Item>

                <Form.Item label="Hostname">
                    <Input
                        defaultValue={value.getHostname()}
                        onChange={(ev) => {
                            const hostname = ev.target.value
                            debounce(() => {
                                value.setHostname(hostname)
                                setTmpValue(value)
                            })
                        }}
                    />
                </Form.Item>

                <Form.Item label="Preview Domain">
                    <Select<number>
                        defaultValue={value.getPreviewDomainId()}
                        onChange={(previewDomainId) => {
                            value.setPreviewDomainId(previewDomainId)
                            setTmpValue(value)
                        }}
                    >
                        {availablePreviewDomains.map((d) => (
                            <Select.Option key={d.id} value={d.id}>
                                {d.display_name}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    label={
                        <span>
                            Documentation Link
                            {!!value.getDocumentationLink() && (
                                <span className="url-preview" onClick={() => window.open(value.getDocumentationLink())}>
                                    <LinkOutlined />
                                </span>
                            )}
                        </span>
                    }
                >
                    <Input
                        defaultValue={value.getDocumentationLink()}
                        onChange={(ev) => {
                            const url = ev.target.value
                            debounce(() => {
                                value.setDocumentationLink(url)
                                setTmpValue(value)
                            })
                        }}
                    />
                </Form.Item>

                <Form.Item label="Support Email Address">
                    <Input
                        defaultValue={value.getSupportEmailAddress()}
                        onChange={(ev) => {
                            const addr = ev.target.value
                            debounce(() => {
                                value.setSupportEmailAddress(addr)
                                setTmpValue(value)
                            })
                        }}
                    />
                </Form.Item>

                <MediaFormItem
                    label="Logo URL"
                    value={value.getLogoUrl()}
                    onChange={(url) => {
                        debounce(() => {
                            value.setLogoUrl(url)
                            setTmpValue(value)
                        })
                    }}
                />

                <MediaFormItem
                    label="Primary Icon URL"
                    value={value.getPrimaryIconUrl()}
                    onChange={(url) => {
                        debounce(() => {
                            value.setPrimaryIconUrl(url)
                            setTmpValue(value)
                        })
                    }}
                />

                <MediaFormItem
                    label="Sidebar Icon URL"
                    value={value.getSidebarIconUrl()}
                    onChange={(url) => {
                        debounce(() => {
                            value.setSidebarIconUrl(url)
                            setTmpValue(value)
                        })
                    }}
                />

                <MediaFormItem
                    label="Favicon Icon URL"
                    value={currFaviconUrl}
                    onChange={(href) => {
                        debounce(() => {
                            const favicon: IFaviconResource = {
                                rel: 'shortcut icon',
                                href,
                            }

                            value.setFaviconResources(!href ? null : [favicon])
                            setTmpValue(value)
                        })
                    }}
                />

                <MediaFormItem
                    label="Login Background URL"
                    value={value.getLoginBackgroundUrl()}
                    onChange={(url) => {
                        debounce(() => {
                            value.setLoginBackgroundUrl(url)
                            setTmpValue(value)
                        })
                    }}
                />

                <Row className="color-selections" justify="space-between" align="top">
                    <Col span={8}>
                        <Form.Item label="Primary Color">
                            <ColorPickerPopover
                                defaultColor={props.whiteLabel?.getPrimaryColorHex()}
                                color={value.getPrimaryColorHex()}
                                onChange={(hex) => {
                                    value.setPrimaryColorHex(hex)
                                    setTmpValue(value)
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="Secondary Color">
                            <ColorPickerPopover
                                defaultColor={props.whiteLabel?.getSecondaryColorHex()}
                                color={value.getSecondaryColorHex()}
                                onChange={(hex) => {
                                    value.setSecondaryColorHex(hex)
                                    setTmpValue(value)
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="Tertiary Color">
                            <ColorPickerPopover
                                defaultColor={props.whiteLabel?.getTertiaryColorHex()}
                                color={value.getTertiaryColorHex()}
                                onChange={(hex) => {
                                    value.setTertiaryColorHex(hex)
                                    setTmpValue(value)
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Drawer>
    )
}

export default WhiteLabelEditor
